'use client';

import useEmblaCarousel from 'embla-carousel-react';
import { Media, Product } from '@/payload-types';
import { MediaComponent } from '@/components/Media';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Thumb } from '@/components/ProductHeroImage/Thumb';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';

interface IImageCarouselProps {
  list?: (Media | string)[] | null;
  variants?: Product['variants'];
  selectedVariant?: string;
  className?: string;
}

export default function ProductHeroImage(props: IImageCarouselProps) {
  const { list, variants, selectedVariant, className } = props;

  // CAROUSEL
  const [emblaRef, emblaMainApi] = useEmblaCarousel();
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return;
      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi, emblaThumbsApi],
  );

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;
    setSelectedIndex(emblaMainApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaMainApi) return;
    onSelect();

    emblaMainApi.on('select', onSelect).on('reInit', onSelect);
  }, [emblaMainApi, onSelect]);

  const onClickPrev = () => {
    emblaMainApi?.scrollPrev();
    onSelect();
  };

  const onClickNext = () => {
    emblaMainApi?.scrollNext();
    onSelect();
  };

  // VARIANTS
  const variantsWithImg = useRef<string[]>([]);

  const variantImages = variants?.map(variant => variant.variantImage).filter(Boolean) || [];
  useEffect(() => {
    variantsWithImg.current = variants
      ?.filter(v => v.variantImage)
      ?.map(v => v.id) as string[] || [];
  }, [variants]);

  // Scroll to the variant image if a new variant is selected.
  useEffect( () => {
    if (!selectedVariant) return;
    const newImgIndex = variantsWithImg.current.findIndex(v => v === selectedVariant);
    if (newImgIndex === -1) return;

    emblaMainApi?.scrollTo((list?.length || 0) + newImgIndex);
    onSelect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant] );

  const imageList = [...(list || []), ...variantImages];

  if (!imageList?.length) {
    return (
      <div className="w-full h-80 rounded-lg bg-neutral-200 flex items-center justify-center">
        <p>No image</p>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className='sticky top-8'>
        {/* MAIN IMAGE */}
        <div ref={emblaRef} className="relative overflow-hidden">
          <div className="flex will-change-transform">
            {imageList?.map((item, i) => (
              <div
                className="min-w-0 flex-[0_0_100%] *:h-full cursor-grab active:cursor-grabbing select-none"
                key={`${i.toString()}`}
              >
                <MediaComponent
                  resource={{ ...(item as Media), width: 1280, height: 800 }}
                  imgClassName="object-contain rounded-lg m-auto h-126"
                />
              </div>
            ))}
          </div>
        </div>
        {/* THUMBS */}
        <div className="flex items-center mt-6 gap-3">
          <button
            className="p-2 rounded-full hover:bg-gray-100 disabled:bg-gray-100 cursor-pointer disabled:cursor-not-allowed"
            onClick={onClickPrev}
            disabled={selectedIndex === 0}
          >
            <ChevronLeft />
          </button>
          <div className="overflow-hidden w-full" ref={emblaThumbsRef}>
            <div className="flex flex-row -ml-3">
              {imageList.map((item, index) => (
                <Thumb
                  key={index}
                  onClick={() => onThumbClick(index)}
                  selected={index === selectedIndex}
                >
                  <MediaComponent
                    resource={{ ...(item as Media), width: 500, height: 500 }}
                    className="rounded-md overflow-hidden"
                    imgClassName="object-contain h-full max-h-20"
                  />
                </Thumb>
              ))}
            </div>
          </div>
          <button
            className="p-2 rounded-full hover:bg-gray-100 disabled:bg-gray-100 cursor-pointer disabled:cursor-not-allowed"
            onClick={onClickNext}
            disabled={selectedIndex === imageList.length - 1}
          >
            <ChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
}
